import type { WebKilnEditorAdapter } from '../editor/webkiln-editor-adapter';
import type { LocalProjectStorage } from '../storage/project-storage';
import type { WebKilnProject } from '../types';
import { CloudAutosaveQueue, type SyncState } from './autosave-queue';
import type { ProjectSyncRepository, SiteProject } from './contracts';

export class CloudEditorSync {
  private readonly queue: CloudAutosaveQueue;
  private unsubscribe: (() => void) | undefined;
  private conflict: SiteProject | null = null;
  private syncTimer: number | undefined;
  private lastSavedPayload = '';
  private readonly onProjectSaved = () => {
    window.clearTimeout(this.syncTimer);
    this.captureAndQueue();
  };
  constructor(
    private readonly adapter: WebKilnEditorAdapter,
    private readonly project: WebKilnProject,
    private readonly storage: LocalProjectStorage,
    repository: ProjectSyncRepository,
    siteId: string,
    revision: number,
    private readonly onState: (state: SyncState) => void,
    private readonly onConflict: (remote: SiteProject) => void,
  ) {
    this.queue = new CloudAutosaveQueue(repository, siteId, revision, (state) => {
      this.onState(state);
      if (state === 'conflict') {
        void repository
          .getProject(siteId)
          .then((remote) => {
            this.conflict = remote;
            this.onConflict(remote);
          })
          .catch(() => undefined);
      }
    });
  }
  start(): void {
    const page = this.project.pages.find((item) => item.id === this.project.currentPageId);
    this.lastSavedPayload = JSON.stringify(page?.projectData ?? {});
    this.unsubscribe = this.adapter.subscribe('update', () => this.scheduleCapture());
    if (typeof document !== 'undefined') {
      document.addEventListener('webkiln:project-saved', this.onProjectSaved);
    }
  }
  scheduleCapture(): void {
    window.clearTimeout(this.syncTimer);
    this.syncTimer = window.setTimeout(() => this.captureAndQueue(), 500);
  }
  captureAndQueue(force = false): void {
    const page = this.project.pages.find((item) => item.id === this.project.currentPageId);
    if (!page) return;
    const currentData = this.adapter.exportProjectData();
    const serialized = JSON.stringify(currentData);
    if (!force && serialized === this.lastSavedPayload) {
      return;
    }
    this.lastSavedPayload = serialized;
    page.projectData = currentData;
    page.updatedAt = new Date().toISOString();
    this.storage.save(this.project);
    this.queue.queue(this.project);
  }
  async flush(): Promise<SiteProject | null> {
    return this.queue.flush();
  }
  async saveBeforePublish(): Promise<void> {
    window.clearTimeout(this.syncTimer);
    this.captureAndQueue(true);
    await this.queue.flushAll();
  }
  setConflict(remote: SiteProject): void {
    this.conflict = remote;
  }
  get remoteConflict(): SiteProject | null {
    return this.conflict;
  }
  get currentRevision(): number {
    return this.queue.currentRevision;
  }
  advanceRevision(revision: number): void {
    this.queue.unblockWithRevision(revision);
  }
  reloadRemote(remote: SiteProject): void {
    Object.assign(this.project, remote.project);
    const remoteData =
      this.project.pages.find((page) => page.id === this.project.currentPageId)?.projectData ?? {};
    this.lastSavedPayload = JSON.stringify(remoteData);
    this.adapter.loadProjectData(remoteData);
    this.storage.save(this.project);
    this.queue.unblockWithRevision(remote.serverRevision);
    this.conflict = null;
    this.onState('saved');
  }
  destroy(): void {
    window.clearTimeout(this.syncTimer);
    this.unsubscribe?.();
    if (typeof document !== 'undefined') {
      document.removeEventListener('webkiln:project-saved', this.onProjectSaved);
    }
  }
}
